'use client';
import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import FloorPlanIcon from '../Icons/FloorPlan';
import { FloorImageContent } from '@/services/types';
import { getVillaHouseFloors } from '@/actions/villages';
import 'viewerjs/dist/viewer.css';
import './styles.css';

type FloorPlanImageData = {
  x: number;
  y: number;
  left: number;
  top: number;
  width: number;
  height: number;
  ratio: number;
  oldRatio: number;
  aspectRatio?: number;
  naturalWidth?: number;
  naturalHeight?: number;
};

type FloorPlanViewer = Viewer & {
  imageData: FloorPlanImageData;
  initialImageData: FloorPlanImageData;
  isShown: boolean;
  title?: HTMLElement;
  resize: () => void;
  renderImage: () => void;
  initImage: (done?: () => void) => void;
  imageInitializing?: { abort?: () => void } | false;
};

function floorPlanMinZoomRatio() {
  return window.innerWidth > 900 ? 0.2 : 0.05;
}

function setFloorPlanMinZoomRatio(viewer: Viewer) {
  (
    viewer as Viewer & { options: { minZoomRatio?: number } }
  ).options.minZoomRatio = floorPlanMinZoomRatio();
}

function isLandscapeViewport() {
  return window.innerWidth > window.innerHeight;
}

function restoreDefaultFloorPlan(viewer: FloorPlanViewer) {
  viewer.imageData = { ...viewer.initialImageData };
  viewer.renderImage();
}

function isTitleInsideViewport(viewer: FloorPlanViewer) {
  const title = viewer.title;

  if (!title || !title.offsetHeight) return true;

  return title.offsetTop + title.offsetHeight <= window.innerHeight + 1;
}

function fitFloorPlanToViewport(viewer: FloorPlanViewer) {
  const naturalWidth = viewer.imageData?.naturalWidth;
  const naturalHeight = viewer.imageData?.naturalHeight;
  const title = viewer.title;

  if (!naturalWidth || !naturalHeight) return false;

  const viewerWidth = window.innerWidth;
  const bottomInset = title?.offsetHeight || 0;
  const viewerHeight = Math.max(window.innerHeight - bottomInset, 1);
  const aspectRatio = naturalWidth / naturalHeight;
  let width = viewerWidth;
  let height = viewerHeight;

  if (viewerHeight * aspectRatio > viewerWidth) {
    height = viewerWidth / aspectRatio;
  } else {
    width = viewerHeight * aspectRatio;
  }

  width = Math.min(width * 0.9, naturalWidth);
  height = Math.min(height * 0.9, naturalHeight);

  const left = (viewerWidth - width) / 2;
  const top = (viewerHeight - height) / 2;
  const imageData: FloorPlanImageData = {
    left,
    top,
    x: left,
    y: top,
    width,
    height,
    oldRatio: 1,
    ratio: width / naturalWidth,
    aspectRatio,
    naturalWidth,
    naturalHeight,
  };

  viewer.imageData = imageData;
  viewer.initialImageData = { ...imageData };
  viewer.renderImage();

  return true;
}

function keepLatestFloorPlanSizing(viewer: FloorPlanViewer) {
  const initImage = viewer.initImage.bind(viewer);

  viewer.initImage = (done) => {
    const pending = viewer.imageInitializing;

    if (pending && pending.abort) pending.abort();

    if (!isTitleInsideViewport(viewer) && fitFloorPlanToViewport(viewer)) {
      viewer.imageInitializing = false;
      done?.();
      return;
    }

    initImage(done);
  };
}

function refitFloorPlan(viewer: FloorPlanViewer) {
  setFloorPlanMinZoomRatio(viewer);

  if (!viewer.isShown) return;

  viewer.resize();
}

type Props = {
  villageId: number;
  houseId: number;
  lang: string;
  seeFloorPlanLabel: string;
};

//pipeline trigger

export default function FloorPlanViewerCanvas({
  villageId,
  houseId,
  lang,
  seeFloorPlanLabel,
}: Props) {
  const [viewerInstance, setViewerInstance] = useState<Viewer | null>(null);
  const [images, setImages] = useState<FloorImageContent[]>([]);
  const viewerRef = useRef<HTMLDivElement>(null);
  const viewerInstanceRef = useRef<Viewer | null>(null);

  useEffect(() => {
    async function getFloorImages() {
      const floors = await getVillaHouseFloors(villageId, houseId);
      const filteredContent: FloorImageContent[] = [];

      floors.forEach((floor) => {
        floor.translation.map((translation) => {
          if (translation.locale === lang) {
            filteredContent.push(translation);
          }
        });
      });
      setImages(filteredContent);
    }
    getFloorImages();
  }, [villageId, houseId, lang]);

  useEffect(() => {
    let viewer: Viewer | null = null;
    let cancelled = false;

    async function handleViewerInstance() {
      const Viewer = (await import('viewerjs')).default;
      const imagesContainer = viewerRef.current;

      if (cancelled || !imagesContainer) return;

      viewer = new Viewer(imagesContainer, {
        inline: false,
        button: true,
        navbar: false,
        title: function (image: HTMLImageElement) {
          return image.alt;
        },
        toolbar: {
          zoomIn: 1,
          zoomOut: 1,
          oneToOne: 0,
          reset: 1,
          prev: 1,
          play: 0,
          next: 1,
          rotateLeft: 0,
          rotateRight: 0,
          flipHorizontal: 0,
          flipVertical: 0,
        },
        tooltip: false,
        movable: true,
        zoomable: true,
        rotatable: false,
        scalable: false,
        transition: true,
        fullscreen: true,
        keyboard: true,
        zIndex: 9999,
        minZoomRatio: floorPlanMinZoomRatio(),
        maxZoomRatio: 1,
        zoom(event: Viewer.ZoomEvent) {
          const current = viewer as FloorPlanViewer | null;
          const initialRatio = current?.initialImageData?.ratio;

          if (!current || initialRatio == null) return;

          const { ratio, oldRatio } = event.detail;

          if (oldRatio > initialRatio && ratio <= initialRatio) {
            event.preventDefault();
            restoreDefaultFloorPlan(current);
          }
        },
      });
      if (cancelled) {
        viewer.destroy();
        viewer = null;
        return;
      }
      keepLatestFloorPlanSizing(viewer as FloorPlanViewer);
      viewerInstanceRef.current = viewer;
      setViewerInstance(viewer);
    }

    let landscape = isLandscapeViewport();
    let refitTimer = 0;

    function scheduleRefit() {
      window.clearTimeout(refitTimer);
      let attempts = 0;

      const tryRefit = () => {
        const current = viewerInstanceRef.current as FloorPlanViewer | null;

        if (!current) return;

        attempts += 1;

        if (!isTitleInsideViewport(current) && attempts < 8) {
          refitTimer = window.setTimeout(tryRefit, 80);
          return;
        }

        refitFloorPlan(current);
      };

      refitTimer = window.setTimeout(tryRefit, 320);
    }

    function handleResize() {
      const current = viewerInstanceRef.current;

      if (!current) return;

      setFloorPlanMinZoomRatio(current);
      const nextLandscape = isLandscapeViewport();

      if (nextLandscape === landscape) return;

      landscape = nextLandscape;
      scheduleRefit();
    }

    function handleOrientationChange() {
      landscape = isLandscapeViewport();
      scheduleRefit();
    }

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    handleViewerInstance();

    return () => {
      cancelled = true;
      window.clearTimeout(refitTimer);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      viewer?.destroy();
      viewerInstanceRef.current = null;
    };
  }, []);

  const handleShowViewer = () => {
    if (viewerInstance) {
      viewerInstance.show();
    }
  };

  useEffect(() => {
    if (viewerInstance) {
      viewerInstance.update();
    }
  }, [viewerInstance, images]);

  return (
    <>
      <li
        onClick={handleShowViewer}
        className="text-primary flex items-center cursor-pointer">
        <FloorPlanIcon className="fill-current w-6 h-6 mr-2" />
        {seeFloorPlanLabel}
      </li>
      <div
        ref={viewerRef}
        aria-hidden="true"
        className="floor-plan-viewer-source">
        <ul>
          <li>
            {images.map(({ image, title }) => {
              if (image) {
                return (
                  <Image
                    key={image.filename}
                    src={`${process.env.NEXT_PUBLIC_S3_URI}/uploads/${image.path}${image.filename}`}
                    alt={title}
                    width={1600}
                    height={1100}
                    unoptimized
                    draggable={false}
                    loading="eager"
                  />
                );
              }
            })}
          </li>
        </ul>
      </div>
    </>
  );
}
